import React from 'react';
import { Divider, Popconfirm, Row, Tooltip } from 'antd';
import { PiPencil } from 'react-icons/pi';
import { FiExternalLink } from 'react-icons/fi';
import { IoEarth } from 'react-icons/io5';
import { Delete } from '@/lib/reactIconly';
import { Eye } from 'iconsax-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { RiErrorWarningLine } from 'react-icons/ri';
import isEmpty from 'lodash/isEmpty';
import { Link } from 'react-router';
import { renderDatetimeColumnWithUtcZeroTimeBool } from '@/lib/renderDatetimeColumnWithUtcZeroTimeBool';
import { HasRights, PrivateComponent } from '@/components/HasRights/HasRights';
import { Permissions } from '@/const/permissions';
import { EVENT_TYPES_MAP, SOURCES_MAP } from '@/const/sources';
import { renderReleasedToColumn } from '@/lib/renderReleasedToColumn';
import { EMPTY_IMAGE_PLACEHOLDER } from '@/const/system';
import UnpublishedChangesWarningTitle from '@/components/UnpublishedChangesWarningTitle';
import { IntlMessages } from '@/components';
import appRoutes from '@/configs/routes';
import { FEATURES_SOURCE_MAP } from '../../../../Featured/pages/PageFeatures/PageFeatures.const';
import getDictionaryIdFromDynamicData from '../../../../../lib/getDictionaryIdFromDynamicData';
import PublishedTag from '../../../../../components/PublishedTag';
import CollapsibleText from '../../../../../components/CollapsibleText';



export const EVENTS_DICT_SOURCE = 'events';

export const getEventsTableColumns = (
  handlePreviewDictionary,
  handleDelete,
  handleClickImage,
  tableSize,
) => [
  {
    title: <IntlMessages id="ui-general-id" />,
    dataIndex: 'id',
    align: 'center',
    width: tableSize.width * 0.05,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-published" />,
    align: 'center',
    dataIndex: 'is_published',
    sorter: true,
    width: tableSize.width * 0.13,
    render: (value, item) => <PublishedTag published={Boolean(value)} publishedDate={item.published_at} />,
  },
  {
    title: <IntlMessages id="ui-general-preview" />,
    dataIndex: 'preview',
    align: 'center',
    width: tableSize.width * 0.13,
    render: (value, item) => (
      <LazyLoadImage
        onClick={() => {
          if (!isEmpty(item?.preview)) {
            handleClickImage(item.preview);
          }
        }}
        style={!isEmpty(item?.preview) ? { cursor: 'pointer' } : {}}
        effect='blur'
        key={item?.id}
        className='hp-cursor-pointer'
        alt="popups preview"
        placeholderSrc={EMPTY_IMAGE_PLACEHOLDER}
        src={value ? `${value}?time=${Date.now()}` : EMPTY_IMAGE_PLACEHOLDER}
      />
    ),
  },
  {
    title: <IntlMessages id="ui-general-title" />,
    dataIndex: 'title',
    width: tableSize.width * 0.10,
    align: 'center',
    sorter: true,
    render: (item, value) => <UnpublishedChangesWarningTitle text={item} data={value} />,
  },
  {
    title: <IntlMessages id="structures-source-collection" />,
    align: 'center',
    dataIndex: 'collection',
    width: tableSize.width * 0.10,
    render: (collection) => {
      return (HasRights([ Permissions.CONTENT.COLLECTIONS.VIEW ]) &&
        !isEmpty(collection?.data?.title) ? (
          <Link style={{ wordBreak: 'break-all' }} to={appRoutes.urlPageCollectionDetails({ urlPrefix: SOURCES_MAP.EVENTS, source: EVENT_TYPES_MAP.EVENT, collectionId: collection?.data?.id })} target="_blank">
            <CollapsibleText text={collection?.data?.title} maxLength={20} />
            <FiExternalLink size={14} className="hp-ml-4" />
          </Link>
        ) : <div><CollapsibleText text={collection?.data?.title} maxLength={20} /></div>
      );
    },
  },
  {
    title: <IntlMessages id="structures-source-external-promo" />,
    align: 'center',
    width: tableSize.width * 0.15,
    render: (values) => {
      return values?.external_promo_ids.length > 0 && (
        values?.external_promos?.data.map((item, index) => {
          return (values?.external_promo_ids.length > 0 ? (
            <div key={item?.id}>
              <Divider style={{ display: index === 0 ? 'none' : 'block' }} />
              {HasRights([ Permissions.FEATURED.FEATURES.EXTERNAL_PROMO.FIND ]) ? (
                <Link
                  target="_blank"
                  style={{ wordBreak: 'break-all' }}
                  to={appRoutes.urlPageFeatureDetails({
                    urlPrefix: SOURCES_MAP.EVENTS,
                    featureType: EVENT_TYPES_MAP.EVENT,
                    featureSource: FEATURES_SOURCE_MAP.EXTERNAL_PROMO,
                    featureId: item?.id,
                  })}
                >
                  <CollapsibleText text={item?.title} maxLength={20} />
                  <FiExternalLink size={14} className="hp-ml-4" />
                </Link>
              ) : <CollapsibleText text={item?.title} maxLength={20} />}
            </div>
          ) : <span>EMPTY_VALUE_PLACEHOLDER</span>);
        })
      );
    },
  },
  {
    title: <IntlMessages id="structures-source-popup" />,
    align: 'center',
    width: tableSize.width * 0.09,
    render: (values) => {
      return values?.popup_ids.length > 0 && (
        values?.popups?.data.map((item, index) => {
          return (values?.popup_ids.length > 0 ? (
            <div key={item?.id}>
              <Divider style={{ display: index === 0 ? 'none' : 'block' }} />
              {HasRights([ Permissions.OTHER.POPUPS.FIND ]) ? (
                <Link
                  target="_blank"
                  style={{ wordBreak: 'break-all' }}
                  to={appRoutes.urlPagePopupDetails({ urlPrefix: SOURCES_MAP.EVENTS, source: EVENT_TYPES_MAP.EVENT, popupId: item?.id })}
                >
                  <CollapsibleText text={item?.title} maxLength={20} />
                  <FiExternalLink size={14} className="hp-ml-4" />
                </Link>
              ) : <CollapsibleText text={item?.title} maxLength={20} />}
            </div>
          ) : <span>EMPTY_VALUE_PLACEHOLDER</span>);
        })
      );
    },
  },
  {
    title: <IntlMessages id="structures-source-sticker" />,
    align: 'center',
    width: tableSize.width * 0.08,
    render: (values) => {
      return values?.stickers?.data?.length > 0 && (
        values.stickers?.data?.map((item, index) => (
          <div key={item?.id}>
            <Divider style={{ display: index === 0 ? 'none' : 'block' }} />
            {HasRights([ Permissions.OTHER.STICKERS.FIND ]) ? (
              <Link
                target="_blank"
                style={{ wordBreak: 'break-all' }}
                to={appRoutes.urlPageStickerDetails({ urlPrefix: SOURCES_MAP.EVENTS, source: EVENT_TYPES_MAP.EVENT, stickerId: item?.id })}
              >
                <CollapsibleText text={item?.title} maxLength={20} />
                <FiExternalLink size={14} className="hp-ml-4" />
              </Link>
            ) : <CollapsibleText text={item?.title} maxLength={20} />}
          </div>
        ))
      );
    },
  },
  {
    title: <IntlMessages id="ui-general-released-at" />,
    dataIndex: 'released_at',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.13,
    render: (value, item) => renderDatetimeColumnWithUtcZeroTimeBool(value, item),
  },
  {
    title: <IntlMessages id="ui-general-released-to" />,
    dataIndex: 'released_to',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.13,
    render: renderReleasedToColumn,
  },
  ...(HasRights([
    Permissions.OTHER.EVENTS.FIND,
    Permissions.OTHER.EVENTS.EDIT,
    Permissions.OTHER.EVENTS.DELETE,
    Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW,
  ]) ?
    [ {
      title: <IntlMessages id="ui-general-action" />,
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: tableSize.width * 0.10,
      render: (item) => {
        const dictionaryId = getDictionaryIdFromDynamicData(item?.dynamic_data?.data);

        return (
          <Row className="da-h-100" align="middle" justify="center">
            <PrivateComponent allowedPermissions={[ Permissions.OTHER.EVENTS.FIND ]}>
              <Tooltip placement="top" title={<IntlMessages id="ui-general-view" />}>
                <div className="hp-text-right">
                  <Link
                    to={appRoutes.urlPageEventDetails({ eventId: item.id })}
                    target="_blank"
                  >
                    <Eye color="currentColor"
                      size={20}
                      data-testid="view-icon"
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                    />
                  </Link>
                </div>
              </Tooltip>
            </PrivateComponent>
            <PrivateComponent allowedPermissions={[ Permissions.OTHER.EVENTS.EDIT ]}>
              <Tooltip placement="top" title={<IntlMessages id="ui-general-edit" />}>
                <div className="hp-text-right">
                  <Link to={appRoutes.urlPageEventEdit({ eventId: item.id })}>
                    <PiPencil
                      size={20}
                      data-testid="edit-icon"
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                    />
                  </Link>
                </div>
              </Tooltip>
            </PrivateComponent>
            <PrivateComponent allowedPermissions={[ Permissions.OTHER.EVENTS.DELETE ]}>
              {!item.is_published ? (
                <Popconfirm
                  title={<IntlMessages id="ui-general-delete-confirm-message" />}
                  placement="topLeft"
                  onConfirm={() => handleDelete(item.id)}
                  okText={<IntlMessages id="ui-general-yes" />}
                  cancelText={<IntlMessages id="ui-general-no" />}
                  icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip placement="top" title={<IntlMessages id="ui-general-delete" />}>
                    <div className="hp-text-right">
                      <Delete
                        size={20}
                        data-testid="delete-icon"
                        className="hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-black-80 hp-m-4"
                      />
                    </div>
                  </Tooltip>
                </Popconfirm>
              ) : (
                <Tooltip placement="top" title={<IntlMessages id="ui-general-delete-published-notification" />}>
                  <div className="hp-text-right">
                    <Delete
                      disabled
                      size={20}
                      data-testid="delete-icon"
                      className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              )}
            </PrivateComponent>
            <PrivateComponent allowedPermissions={[ Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW ]}>
              {Boolean(dictionaryId) && (
                <Tooltip placement="top" title={<IntlMessages id="ui-general-dictionary" />}>
                  <div className="hp-text-right">
                    <IoEarth
                      size={20}
                      data-testid="dictionary-icon"
                      onClick={() => handlePreviewDictionary(dictionaryId)}
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              )}
            </PrivateComponent>
          </Row>
        );
      },
    } ] : []
  ),
];
